import { forwardRef } from "react"
import { Button as EdsButton } from "../../ui/button"
import { Spinner } from "../../ui/spinner"
import type { ButtonProps } from "./Button"
import type { LucideIcon } from "lucide-react"
import { cn } from "../../../lib/utils"

export interface IconButtonProps extends Omit<ButtonProps, "children"> {
	/**
	 * Icon component to render (Lucide icon or any React icon component)
	 */
	icon: LucideIcon | React.ComponentType<{ className?: string; size?: number | string }>
	/**
	 * Size of the icon in pixels (optional)
	 */
	iconSize?: number
	/**
	 * Additional CSS classes to apply to the icon (optional)
	 */
	iconClassName?: string
}

/**
 * IconButton component that displays an icon.
 * When loading, replaces the icon with a spinner.
 *
 * For icon buttons, the destructive variant uses outline styling with destructive colors
 * instead of a filled background, making it more suitable for icon-only buttons.
 *
 * @example Basic usage
 * ```tsx
 * import { Edit } from "lucide-react"
 *
 * <IconButton icon={Edit} onClick={handleEdit} />
 * ```
 *
 * @example Destructive action
 * ```tsx
 * import { Trash2 } from "lucide-react"
 *
 * <IconButton icon={Trash2} variant="destructive" onClick={handleDelete} />
 * ```
 *
 * @example With loading
 * ```tsx
 * import { Save } from "lucide-react"
 *
 * <IconButton icon={Save} loading={saving} onClick={handleSave} />
 * ```
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
	(
		{ icon: Icon, iconSize, iconClassName, loading, disabled, variant = "ghost", size = "icon", className, ...props },
		ref
	) => {
		const isDestructive = variant === "destructive"

		return (
			<EdsButton
				ref={ref}
				variant={isDestructive ? "outline" : variant}
				size={size}
				disabled={disabled || loading}
				className={cn(
					"cursor-pointer",
					isDestructive && "text-destructive hover:text-destructive hover:bg-destructive/10",
					className
				)}
				aria-label="Icon button"
				{...props}>
				{loading ? <Spinner /> : <Icon size={iconSize} className={iconClassName} />}
			</EdsButton>
		)
	}
)

IconButton.displayName = "IconButton"
